<script lang="ts" setup>
import { watch, onUnmounted } from 'vue'

interface Props {
  isOpen: boolean
  onClose?: () => void
}

interface Emits {
  (e: 'close'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const disableScroll = () => {
  document.body.style.overflow = 'hidden'
  document.documentElement.style.overflow = 'hidden'
}

const enableScroll = () => {
  document.body.style.overflow = ''
  document.documentElement.style.overflow = ''
}

watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    disableScroll()
  } else {
    enableScroll()
  }
})

onUnmounted(enableScroll)

const handleOverlayClick = (event: MouseEvent) => {
  if (event.target !== event.currentTarget) return

  if (props.onClose) {
    props.onClose()
  }
  emit('close')
}

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    if (props.onClose) {
      props.onClose()
    }
    emit('close')
  }
}
</script>

<template>
  <div
      v-if="isOpen"
      class="modal-overlay"
      @click="handleOverlayClick"
      @keydown="handleKeydown"
      tabindex="-1"
  >
    <div
        class="modal-content"
        @click.stop
        role="dialog"
        aria-modal="true"
    >
      <slot />
    </div>
  </div>
</template>

<style lang="css" scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999999999;
  outline: none;
}

.modal-content {
  border-radius: 8px;
  padding: 24px;
  background: #FFFFFF;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  min-width: 320px;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  margin: 0;
}

:global(.overlay-container) {
  z-index: 1000000;
}
</style>